import {
    ServiceQueries,
    query,
    serviceQueries,
    ServiceEvents,
    ServiceCommands,
    events,
    on,
    emit
} from '@estee/elc-service-bus';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { ViewController } from '../../ViewController';
import { ServiceBusController } from '../ServiceBusController';
import { BaseService } from '../BaseService';

type diContainerType = { get<T>(key: string): T };
export type ContainerImport = () => Promise<{ diContainer: diContainerType }>;

export type ServiceBusControllerBody = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: Promise<(...args: any[]) => any> | Function;
} & { onEcommStarted(): void };

interface IServiceStartParams {
    serviceName: string;
    diContainerImport: ContainerImport;
    serviceKey: string;
    serviceBusControllerKey?: string;
    serviceBusBaseClass?: ServiceBusController & ServiceBusControllerBody;
}

export class ServiceManagerClass {
    public serviceName: string;
    private containerImport: ContainerImport;
    private diContainer: diContainerType;
    private serviceKey: string;
    private service: BaseService;
    private serviceBusControllerKey?: string;
    private serviceBusBaseClass?: ServiceBusController & ServiceBusControllerBody;
    private logger: ELCLogger;
    private ecommStarted: boolean = false;
    private startPromise: Promise<void>;

    get serviceHasStarted(): boolean {
        return Boolean(this.service && this.service.isStarted);
    }

    private async getContainer() {
        if (!this.diContainer) {
            this.diContainer = (await this.containerImport()).diContainer;
        }

        return this.diContainer;
    }

    public setServiceStartParams({
        serviceName,
        diContainerImport,
        serviceKey,
        serviceBusControllerKey,
        serviceBusBaseClass
    }: IServiceStartParams) {
        this.serviceName = serviceName;
        this.containerImport = diContainerImport;
        this.serviceKey = serviceKey;
        this.serviceBusControllerKey = serviceBusControllerKey;
        this.serviceBusBaseClass = serviceBusBaseClass;
        this.logger = new ELCLogger({ serviceName });
    }

    public getServiceBusControllerMethod(functionName: string) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return async (...args: any[]) => {
            await this.startService();
            const diContainer = await this.getContainer();
            const srvBusController = diContainer.get<ServiceBusController>(
                this.serviceBusControllerKey || ''
            );
            const method = (srvBusController as ServiceBusControllerBody)[functionName] as Function;

            return method.call(null, ...args);
        };
    }

    public setDecoratorOnFunction(decorator: Function) {
        return ([trigger, functionName]: [
            trigger: keyof ServiceQueries | keyof ServiceEvents | keyof ServiceCommands,
            functionName: string
        ]) => {
            if (!this.serviceBusBaseClass || !this.serviceBusControllerKey) {
                throw new Error(`
                    ServiceBus hasn't been provided to ServiceManager at setServiceStartParams method.
                    ServiceBus: ${
                        this.serviceBusBaseClass ? 'ServiceBus found' : this.serviceBusBaseClass
                    }.
                    ServiceBusKey: (${this.serviceBusControllerKey})
                `);
            }

            decorator(trigger)(this.serviceBusBaseClass.__proto__, functionName);
            this.serviceBusBaseClass[functionName] = this.getServiceBusControllerMethod(
                functionName
            );
        };
    }

    @on(events.ECOMM_STARTED, { replay: true })
    public setEcommHasStarted = () => {
        this.ecommStarted = true;
    };

    private onStart = (cb: (error?: Error) => void) => async () => {
        let error: Error | undefined;
        try {
            if (this.serviceHasStarted) {
                cb();
                return;
            }
            if (!this.ecommStarted) {
                return;
            }

            await this.getContainer();
            this.service = this.diContainer.get<BaseService>(this.serviceKey);
            const config = await query(serviceQueries.GET_CONFIG, ['ecommConfig']);
            // If ecommConfig is not available them EcommApp hasn't started yet
            if (!config || !config.ecommConfig) {
                throw new Error('EcommConfig not found');
            }

            await this.service.start(config.ecommConfig);
            emit(events.SERVICE_STARTED, { serviceName: this.serviceName });
        } catch (err) {
            error = err;
            this.logger.error({
                triggerType: TriggerType.render,
                message: err.message,
                payload: {
                    error: err
                }
            });
        } finally {
            cb(error);
        }
    };

    public async startService() {
        if (!this.startPromise) {
            this.startPromise = new Promise((resolve, reject) => {
                let timeoutId: NodeJS.Timeout | null = null;
                timeoutId = setInterval(
                    this.onStart((error?: Error) => {
                        if (timeoutId) {
                            clearInterval(timeoutId);
                        }
                        if (error) {
                            reject(error);
                        } else {
                            resolve();
                        }
                    }),
                    200
                );
            });
        }

        return this.startPromise;
    }

    public getController(controllerKey?: string): ViewController | undefined {
        if (!this.diContainer) {
            const message = "Can't request controller without starting the service first";

            this.logger.error({
                triggerType: TriggerType.render,
                message,
                payload: {
                    serviceName: this.serviceName,
                    context: {
                        method: 'getController method on ServiceManager'
                    }
                }
            });

            throw new Error(message);
        }

        const controller = controllerKey
            ? this.diContainer.get<ViewController>(controllerKey)
            : undefined;

        return controller;
    }
}

export const ServiceManager = new ServiceManagerClass();
